import { useTranslation } from "react-i18next";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { Info } from "../../../components/Info";
import { Fragment, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { permissionActions } from "../../../redux/permission/permission.slice";
import { moduleActions } from "../../../redux/module/module.slice";
import { Button } from "../../../components/Button";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { thresholdActions } from "../../../redux/threshold/threshold.slice";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { Tooltip } from "../../../utils/local-antd";
import { ImageCreateEdit } from "../../../components/ImageCreateEdit";
import { UserProfileWrapper } from "../../../components/UserProfileWrapper";
import { SettingsManageAccountsAddAccountModal } from "../components/SettingsManageAccountsAddAccountModal";
import { SettingsWrapper } from "../components/SettingsWrapper";
import { faUser } from "@fortawesome/free-solid-svg-icons/faUser";
import { faCircleUser } from "@fortawesome/free-solid-svg-icons/faCircleUser";
import { UserLinkedAccountsLinkedAccountEntry } from "../../../model/user/dto/UserLinkedAccountsLinkedAccountEntryDto";
import { UserLinkedAccountsStatus } from "../../../model/user/types/UserLinkedAccountsStatus.enum";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons/faCheckCircle";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import { standardDate } from "../../../utils/Date";
import styled from "styled-components";
import { Spacing } from "../../../theme/Spacing";
import { Rounded } from "../../../theme/Rounded";
import { Clickable } from "../../../components/Clickable";
import { faShuffle } from "@fortawesome/free-solid-svg-icons/faShuffle";
import { faRemove } from "@fortawesome/free-solid-svg-icons/faRemove";
import { WarningModal } from "../../../components/WarningModal";
import { userActions } from "../../../redux/user/user.slice";
import { AtiraToast } from "../../../utils/AtiraToast";
import { faCalendar } from "@fortawesome/free-regular-svg-icons/faCalendar";
import { faCalendarCheck } from "@fortawesome/free-solid-svg-icons/faCalendarCheck";
import { TestIDs } from "../../../tests/types/testIds";
import { openImpersonationTab } from "../../../utils/openImpersonationTab";
import { ImpersonationKind } from "../../../model/user/types/ImpersonationKind.enum";

const StyledWrapper = styled(Flex)`
  flex-direction: column;
  justify-content: space-between;
  gap: ${Spacing.s};
  padding: ${Spacing.s};
  border-radius: ${Rounded.md};
  width: 100%;

  &:hover {
    background-color: ${({ theme }) => `${theme.lightMain}22`};
  }
`;

export const SettingsManageAccounts: React.FC = () => {
  const [addAccountModalOpen, setAddAccountModalOpen] = useState(false);
  const [removeAccountId, setRemoveAccountId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const onRemoveAccount = async () => {
    if (!removeAccountId) {
      return;
    }

    try {
      setLoading(true);
      await dispatch(
        userActions.manageAccountsRemove({
          userAccountId: removeAccountId,
          userId,
        }),
      ).unwrap();
      await dispatch(userActions.getLoggedInUser({ userId })).unwrap();
      AtiraToast.success(
        t("settings.manageAccounts.action.removeAccount.onsSuccess"),
      );
      setRemoveAccountId(null);
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  const onSwitchToAccount = async (accountId: string) => {
    try {
      setLoading(true);
      AtiraToast.info(
        t("settings.manageAccounts.action.switchAccount.inProgress"),
      );
      const payload = await dispatch(
        userActions.manageAccountsSwitch({
          switchAccountId: accountId,
          userId,
        }),
      ).unwrap();
      openImpersonationTab(
        payload.token,
        payload.user,
        ImpersonationKind.LINKED,
        t("settings.manageAccounts.action.switchAccount.popupBlocked"),
      );
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(moduleActions.getAllModuleDefinitions({ userId }));
    dispatch(permissionActions.getMembers({ userId }));
    dispatch(thresholdActions.getThresholds({ userId }));
  }, [dispatch, userId]);

  return (
    <SettingsWrapper>
      <Text fontWeight={"bold"}>
        {t("settings.manageAccounts.subheader.title")}
      </Text>
      <Info info={t("settings.manageAccounts.description")} />
      <Flex flexDirection="column" gap="s">
        <Flex
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="center"
          gap="l"
        >
          {[
            { user, userId: user._id } as UserLinkedAccountsLinkedAccountEntry,
            ...(user?.linkedAccounts?.linkedAccounts || []),
          ].map((account, index) => {
            const isValid = account?.status === UserLinkedAccountsStatus.VALID;

            return (
              <StyledWrapper key={account.userId}>
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  gap="s"
                >
                  <Flex alignItems="center" gap="s">
                    <UserProfileWrapper>
                      {account?.user?.picture ? (
                        <ImageCreateEdit src={account.user.picture} />
                      ) : (
                        <AtiraIcon icon={faCircleUser} size="3x" color="main" />
                      )}
                    </UserProfileWrapper>
                    <Text fontSize="s" align="center">
                      {account.user?.email}
                    </Text>
                  </Flex>
                  <Flex gap="m">
                    {index !== 0 ? (
                      <Fragment>
                        <Tooltip
                          title={t("settings.manageAccounts.row.remove")}
                        >
                          <Clickable
                            dataTestId={TestIDs.general.settings.manageAccounts.remove(
                              account.userId,
                            )}
                            disabled={loading}
                            onClick={() => setRemoveAccountId(account.userId)}
                          >
                            <AtiraIcon
                              icon={faRemove}
                              color="danger"
                              size="lg"
                            />
                          </Clickable>
                        </Tooltip>

                        <Tooltip
                          title={t("settings.manageAccounts.row.switch")}
                        >
                          <Clickable
                            dataTestId={TestIDs.general.settings.manageAccounts.switch(
                              account.userId,
                            )}
                            disabled={loading}
                            onClick={() => onSwitchToAccount(account.userId)}
                          >
                            <AtiraIcon
                              icon={faShuffle}
                              color="main"
                              size="lg"
                            />
                          </Clickable>
                        </Tooltip>
                      </Fragment>
                    ) : null}
                  </Flex>
                </Flex>
                {index === 0 ? (
                  <Flex justifyContent="flex-start" gap="s" alignItems="center">
                    <Tooltip title={t("settings.manageAccounts.mainAccount")}>
                      <span />
                      <AtiraIcon
                        icon={faUser}
                        color="main"
                        size="lg"
                        style={{ opacity: 0.7 }}
                      />
                    </Tooltip>
                    {account.user?.createdAt ? (
                      <Flex gap="xs">
                        <AtiraIcon color="main" icon={faCalendar} />
                        <Text fontSize="xs" color="main">
                          {t("common.createdAt")}{" "}
                          {standardDate<string>(
                            new Date(account.user.createdAt),
                            user,
                          )}
                        </Text>
                      </Flex>
                    ) : null}
                  </Flex>
                ) : (
                  <Flex
                    justifyContent="flex-start"
                    alignItems="center"
                    gap="m"
                    width="100%"
                  >
                    <Flex gap="m">
                      {account.addedAt ? (
                        <Flex gap="xs">
                          <AtiraIcon color="main" icon={faCalendar} />
                          <Text fontSize="xs" color="main">
                            {t("settings.manageAccounts.row.linkedAt", {
                              date: standardDate<string>(
                                new Date(account.addedAt),
                                user,
                              ),
                            })}
                          </Text>
                          {account.lastAccessedAt ? (
                            <Flex gap="xs">
                              <AtiraIcon color="main" icon={faCalendarCheck} />
                              <Text fontSize="xs" color="main">
                                {t("settings.manageAccounts.row.switchedAt", {
                                  date: standardDate<string>(
                                    new Date(account.lastAccessedAt || ""),
                                    user,
                                  ),
                                })}
                              </Text>
                            </Flex>
                          ) : null}
                        </Flex>
                      ) : null}
                    </Flex>
                    <Flex>
                      <Tooltip
                        title={
                          isValid
                            ? t("settings.manageAccounts.row.valid")
                            : t("settings.manageAccounts.row.invalid")
                        }
                      >
                        <span />
                        <AtiraIcon
                          size="lg"
                          color={isValid ? "main" : "danger"}
                          icon={isValid ? faCheckCircle : faXmark}
                        />
                      </Tooltip>
                    </Flex>
                  </Flex>
                )}
              </StyledWrapper>
            );
          })}

          <Button
            onClick={() => setAddAccountModalOpen(true)}
            icon={faPlus}
            hover
            dataTestId={TestIDs.general.settings.manageAccounts.add}
          >
            {t("settings.manageAccounts.add")}
          </Button>
        </Flex>
      </Flex>

      <SettingsManageAccountsAddAccountModal
        open={addAccountModalOpen}
        onClose={() => setAddAccountModalOpen(false)}
      />

      <WarningModal
        isOpen={Boolean(removeAccountId)}
        onClose={() => setRemoveAccountId(null)}
        description={t(
          "settings.manageAccounts.action.removeAccount.modal.description",
        )}
        onConfirm={onRemoveAccount}
        loading={loading}
      />
    </SettingsWrapper>
  );
};
